import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { Observable, switchMap } from 'rxjs';
import { BaseDatatableStore, IBaseDatatableState } from '../../../state/base-datatable-store.service';
import { ELoadStatus } from '../../../state/state.interface';
import {
  HttpUtilitiesService,
  IBaseCrudResponse,
  IBaseResponse,
  IGenericCrudRequestConstructionParameters,
  IGetManyResponse,
} from '../../../utilities/http-utilities.service';
import { TBulkEditFieldSet } from '../fields-field-sets/field-sets/field-sets.interface';
import { TWorkInstructionStepFormUpdate } from './work-instruction-editor/work-instruction-steps/work-instruction-steps.interface';
import {
  IEditWorkInstruction,
  IWorkInstruction,
  TAddWorkInstruction,
  TWorkInstructionBulkEditRequest,
} from './work-instructions.interface';

@Injectable()
export class WorkInstructionsStore extends BaseDatatableStore<IWorkInstruction, IBaseDatatableState<IWorkInstruction>> {
  readonly createOne = this.effect((trigger$: Observable<TAddWorkInstruction>) =>
    trigger$.pipe(
      switchMap((item: TAddWorkInstruction) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.createWorkInstruction(item).pipe(
          tapResponse(
            (): void => {
              this.patchState({ singleCrudLoading: ELoadStatus.success });
            },
            // eslint-disable-next-line no-console,sonarjs/no-duplicate-string
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateOne = this.effect((trigger$: Observable<IEditWorkInstruction>) =>
    trigger$.pipe(
      switchMap((item: IEditWorkInstruction) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.updateWorkInstruction(item).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly updateMany = this.effect((trigger$: Observable<TWorkInstructionBulkEditRequest[]>) =>
    trigger$.pipe(
      switchMap((dto: TWorkInstructionBulkEditRequest[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.bulkEditWorkInstructions(dto).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly duplicateOne = this.effect((trigger$: Observable<{ id: number; name: string }>) =>
    trigger$.pipe(
      switchMap((duplicateData) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.duplicateWorkInstruction(duplicateData).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteOne = this.effect((trigger$: Observable<number>) =>
    trigger$.pipe(
      switchMap((id: number) => {
        this.patchState({ singleCrudLoading: ELoadStatus.loading });

        return this.deleteWorkInstruction(id).pipe(
          tapResponse(
            () => this.patchState({ singleCrudLoading: ELoadStatus.success }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  override readonly deleteBulk = this.effect((trigger$: Observable<number[]>) =>
    trigger$.pipe(
      switchMap((itemIds: number[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.deleteWorkInstructions(itemIds).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly loadItems = this.effect((trigger$: Observable<IGenericCrudRequestConstructionParameters>) =>
    trigger$.pipe(
      switchMap((params: IGenericCrudRequestConstructionParameters) => {
        const httpParams: HttpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);
        this.patchState({ dataLoading: ELoadStatus.loading });

        return this.getWorkInstructions(httpParams).pipe(
          tapResponse(
            (response: IGetManyResponse<IWorkInstruction>) =>
              this.patchState({ data: response.data, dataLoading: ELoadStatus.success, dataTotal: response.total }),
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  readonly bulkUpdateWorkInstructionSteps = this.effect((trigger$: Observable<TWorkInstructionStepFormUpdate[]>) =>
    trigger$.pipe(
      switchMap((workInstructionSteps: TWorkInstructionStepFormUpdate[]) => {
        this.patchState({ bulkCrudLoading: ELoadStatus.loading });

        return this.updateWorkInstructionSteps(workInstructionSteps).pipe(
          tapResponse(
            (response): void => {
              this.patchState({ bulkCrudLoading: ELoadStatus.success, bulkOperationFailedData: response.data });
            },
            // eslint-disable-next-line no-console
            (error) => console.error('Error loading data', error),
          ),
        );
      }),
    ),
  );

  constructor(
    private readonly http: HttpClient,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super({
      bulkCrudLoading: ELoadStatus.initial,
      bulkOperationFailedData: [],
      data: [],
      dataLoading: ELoadStatus.initial,
      dataTotal: 0,
      singleCrudLoading: ELoadStatus.initial,
    });
  }

  private createWorkInstruction(body: TAddWorkInstruction): Observable<IBaseResponse<IWorkInstruction>> {
    return this.http.post<IBaseResponse<IWorkInstruction>>('guides', body);
  }

  private updateWorkInstruction(
    editWorkInstruction: IEditWorkInstruction,
  ): Observable<IBaseResponse<IWorkInstruction>> {
    return this.http.patch<IBaseResponse<IWorkInstruction>>(
      `guides/${editWorkInstruction.id}`,
      editWorkInstruction.dto,
    );
  }

  private duplicateWorkInstruction(duplicateData: {
    id: number;
    name: string;
  }): Observable<IBaseResponse<IWorkInstruction>> {
    return this.http.post<IBaseResponse<IWorkInstruction>>(`guides/duplicate/${duplicateData.id}`, {
      name: duplicateData.name,
    });
  }

  private deleteWorkInstruction(id: number): Observable<IBaseResponse<IWorkInstruction>> {
    return this.http.delete<IBaseResponse<IWorkInstruction>>(`guides/${id}`);
  }

  private getWorkInstructions(params: HttpParams): Observable<IGetManyResponse<IWorkInstruction>> {
    return this.http.get<IGetManyResponse<IWorkInstruction>>('guides', { params });
  }

  private deleteWorkInstructions(ids: number[]): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.delete<IBaseResponse<IBaseCrudResponse[]>>('guides/bulk/delete', {
      body: { payload: ids },
    });
  }

  private updateWorkInstructionSteps(
    steps: TWorkInstructionStepFormUpdate[],
  ): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.patch<IBaseResponse<IBaseCrudResponse[]>>('guide-steps/bulk/update', {
      payload: steps,
    });
  }

  private bulkEditWorkInstructions(
    bulkEditFieldSet: TBulkEditFieldSet[],
  ): Observable<IBaseResponse<IBaseCrudResponse[]>> {
    return this.http.patch<IBaseResponse<IBaseCrudResponse[]>>('guides/bulk/update', { payload: bulkEditFieldSet });
  }
}
